<template>
    <div ref="map-root" class="map">
        <slot />
        <div
            v-show="popup.geos.length"
            ref="popup"
            class="map-popup"
            @pointerenter="cancelClose"
            @pointerleave="scheduleHoverClose"
        >
            <slot
                name="popup"
                :geos="popup.geos"
                :layer-id="popup.layerId"
                :close="closePopup"
            />
        </div>
    </div>
</template>

<script>
import OlMap from "ol/Map";
import Overlay from "ol/Overlay";
import View from "ol/View";
import Feature from "ol/Feature";
import Point from "ol/geom/Point";
import GeoJSON from "ol/format/GeoJSON";
import OSM from "ol/source/OSM";
import XYZ from "ol/source/XYZ";
import VectorSource from "ol/source/Vector";
import TileLayer from "ol/layer/Tile";
import VectorLayer from "ol/layer/Vector";
import { createEmpty, extend, getCenter, isEmpty } from "ol/extent";
import { easeOut } from "ol/easing";
import { fromLonLat, toLonLat } from "ol/proj";
import { useConsentStore } from "js/stores/consentStore.js";
import { crossStyle, regionStyle } from "js/components/maps/styles";

let countriesSource;

function getCountriesSource() {
    if (!countriesSource) {
        countriesSource = new VectorSource({
            format: new GeoJSON(),
            url: "/geojson/countries.geojson",
        });
    }

    return countriesSource;
}

function mainlandExtent(geometry) {
    if (geometry.getType() !== "MultiPolygon") {
        return geometry.getExtent();
    }

    return geometry
        .getPolygons()
        .reduce((largest, polygon) =>
            polygon.getArea() > largest.getArea() ? polygon : largest
        )
        .getExtent();
}

const CLOSE_GRACE = 250;

export default {
    name: "BaseMap",
    props: {
        layers: {
            type: Array,
            default: () => [],
        },
        tile: {
            type: String,
            default: window.tripper?.map ?? "satellite",
        },
        tiles: {
            type: Boolean,
            default: true,
        },
        background: {
            type: String,
            default: undefined,
        },
        fit: {
            type: Boolean,
            default: true,
        },
        fitOptions: {
            type: Object,
            default: () => ({}),
        },
        popupOn: {
            type: String,
            default: null,
        },
        popupDelay: {
            type: Number,
            default: 200,
        },
        highlighted: {
            type: Function,
            default: null,
        },
        pickable: {
            type: Boolean,
            default: false,
        },
    },
    emits: ["feature-click", "feature-hover", "map-click", "map-pick"],
    data() {
        return {
            popup: { geos: [], layerId: null },
            consentStore: useConsentStore(),
        };
    },
    watch: {
        layers() {
            this.syncLayers();
        },
        highlighted() {
            Object.values(this.entries).forEach((entry) => {
                entry.olLayer.changed();
            });
        },
        "consentStore.externalAllowed"() {
            this.tileLayer?.setSource(this.getTileLayerSource());
        },
    },
    created() {
        this.entries = {};
        this.fitted = false;
        this.hoveredGeos = [];
    },
    mounted() {
        this.map = new OlMap({
            target: this.$refs["map-root"],
            layers: [],
            view: new View({
                center: [0, 0],
                zoom: 1,
                enableRotation: false,
            }),
            background: this.background,
        });
        if (this.tiles) {
            this.tileLayer = new TileLayer({
                source: this.getTileLayerSource(),
                zIndex: -1,
            });
            this.map.addLayer(this.tileLayer);
        }
        this.pickerSource = new VectorSource();
        this.pickerLayer = new VectorLayer({
            source: this.pickerSource,
            style: crossStyle,
            zIndex: 1000,
        });
        this.map.addLayer(this.pickerLayer);
        this.overlay = new Overlay({
            element: this.$refs.popup,
            position: [0, 0],
            positioning: "bottom-center",
            offset: [0, -20],
            autoPan:
                this.popupOn === "hover"
                    ? undefined
                    : { animation: { duration: 200 }, margin: 10 },
        });
        this.map.addOverlay(this.overlay);

        this.map.on("loadstart", () => {
            this.map.getTargetElement().classList.add("spinner");
        });
        this.map.on("loadend", () => {
            this.map.getTargetElement().classList.remove("spinner");
        });
        this.map.on("click", (event) => this.onClick(event));
        this.map.on("pointermove", (event) => this.onPointerMove(event));
        this.map
            .getTargetElement()
            .addEventListener("pointerleave", (event) => {
                if (event.pointerType !== "touch") {
                    this.endHover();
                }
            });
        this.map.on("pointerdrag", () => this.closePopup());
        this.map.getView().on("change:resolution", () => this.closePopup());

        this.syncLayers();
    },
    deactivated() {
        this.closePopup();
    },
    beforeUnmount() {
        clearTimeout(this.hoverTimer);
        clearTimeout(this.closeTimer);
        this.map.setTarget(undefined);
    },
    methods: {
        syncLayers() {
            const ids = this.layers.map((layer) => String(layer.id));
            Object.keys(this.entries)
                .filter((id) => !ids.includes(id))
                .forEach((id) => {
                    this.map.removeLayer(this.entries[id].olLayer);
                    delete this.entries[id];
                });
            this.layers.forEach((descriptor, index) => {
                const entry =
                    this.entries[descriptor.id] ?? this.createEntry(descriptor);
                entry.descriptor = descriptor;
                entry.olLayer.setZIndex(index);
                this.fillEntry(entry);
            });
            this.fitOnce();
        },
        createEntry(descriptor) {
            const source =
                descriptor.kind === "regions"
                    ? getCountriesSource()
                    : new VectorSource();
            const entry = { source, descriptor, geoByCode: {} };
            entry.olLayer = new VectorLayer({
                source,
                style: (feature) => this.styleFeature(entry, feature),
            });
            entry.olLayer.set("layerId", String(descriptor.id));
            this.entries[descriptor.id] = entry;
            this.map.addLayer(entry.olLayer);
            if (descriptor.kind === "regions" && !source.getFeatures().length) {
                source.once("featuresloadend", () => this.fitOnce());
            }

            return entry;
        },
        fillEntry(entry) {
            const { source, descriptor } = entry;
            if (descriptor.kind === "regions") {
                entry.geoByCode = Object.fromEntries(
                    descriptor.geos.map((geo) => [geo.country_code, geo])
                );
                entry.olLayer.changed();

                return;
            }
            source.clear();
            source.addFeatures(
                descriptor.geos.map((geo) => {
                    const feature = new Feature(
                        new Point(fromLonLat(geo.getCoordinates()))
                    );
                    feature.set("geo", geo);

                    return feature;
                })
            );
        },
        geoOf(entry, feature) {
            return entry.descriptor.kind === "regions"
                ? entry.geoByCode[feature.getId()]
                : feature.get("geo");
        },
        stateOf(geo) {
            if (!this.highlighted) {
                return "normal";
            }

            return this.highlighted(geo) ? "highlighted" : "dimmed";
        },
        styleFeature(entry, feature) {
            const geo = this.geoOf(entry, feature);
            if (!geo) {
                return regionStyle("standard");
            }

            return entry.descriptor.style(geo, this.stateOf(geo));
        },
        hitsAt(pixel) {
            const result = { picked: false, hits: [] };
            this.map.forEachFeatureAtPixel(pixel, (feature, layer) => {
                if (layer === this.pickerLayer) {
                    result.picked = true;

                    return;
                }
                const layerId = layer.get("layerId");
                const entry = this.entries[layerId];
                const geo = entry && this.geoOf(entry, feature);
                if (geo) {
                    result.hits.push({ geo, layerId });
                }
            });

            return result;
        },
        isTouch(event) {
            return event.originalEvent?.pointerType === "touch";
        },
        onPointerMove(event) {
            if (
                this.popupOn !== "hover" ||
                event.dragging ||
                this.isTouch(event) ||
                this.$refs.popup.contains(event.originalEvent.target)
            ) {
                return;
            }
            const { hits } = this.hitsAt(event.pixel);
            const geos = hits.map((hit) => hit.geo);
            this.map.getTargetElement().style.cursor = geos.length
                ? "pointer"
                : "";
            const unchanged =
                geos.length === this.hoveredGeos.length &&
                geos.every((geo, index) => geo === this.hoveredGeos[index]);
            if (unchanged) {
                return;
            }
            this.hoveredGeos = geos;
            this.$emit("feature-hover", geos, event);
            clearTimeout(this.hoverTimer);
            this.cancelClose();
            if (geos.length) {
                const delay = this.popup.geos.length ? 0 : this.popupDelay;
                this.hoverTimer = setTimeout(
                    () => this.openPopup(hits, event.coordinate),
                    delay
                );
            } else {
                this.scheduleClose();
            }
        },
        scheduleClose() {
            clearTimeout(this.closeTimer);
            this.closeTimer = setTimeout(() => this.closePopup(), CLOSE_GRACE);
        },
        cancelClose() {
            clearTimeout(this.closeTimer);
        },
        scheduleHoverClose(event) {
            if (this.popupOn === "hover" && event.pointerType !== "touch") {
                this.scheduleClose();
            }
        },
        endHover() {
            if (this.popupOn !== "hover" || !this.hoveredGeos.length) {
                return;
            }
            this.hoveredGeos = [];
            this.map.getTargetElement().style.cursor = "";
            this.$emit("feature-hover", [], null);
            this.scheduleClose();
        },
        onClick(event) {
            const { picked, hits } = this.hitsAt(event.pixel);
            const lonLat = toLonLat(event.coordinate);
            this.$emit("map-click", lonLat, event);
            if (picked) {
                this.$emit("map-pick", lonLat);

                return;
            }
            if (hits.length) {
                const tapAsHover =
                    this.popupOn === "hover" && this.isTouch(event);
                if (!tapAsHover) {
                    this.$emit(
                        "feature-click",
                        hits.map((hit) => hit.geo),
                        event
                    );
                }
                if (this.popupOn === "click" || tapAsHover) {
                    this.openPopup(hits, event.coordinate);
                }

                return;
            }
            this.closePopup();
            if (this.pickable) {
                this.pickerSource.clear();
                this.pickerSource.addFeature(
                    new Feature(new Point(event.coordinate))
                );
            }
        },
        async openPopup(hits, coordinate) {
            this.popup = {
                geos: hits.map((hit) => hit.geo),
                layerId: hits[0].layerId,
            };
            await this.$nextTick();
            const [mapWidth] = this.map.getSize();
            const popup = this.$refs.popup;
            popup.style.maxWidth = `${Math.min(300, mapWidth - 32)}px`;
            const [x, y] = this.map.getPixelFromCoordinate(coordinate);
            const vertical = y < popup.offsetHeight + 40 ? "top" : "bottom";
            const left = Math.max(
                8,
                Math.min(
                    x - popup.offsetWidth / 2,
                    mapWidth - popup.offsetWidth - 8
                )
            );
            this.overlay.setPositioning(`${vertical}-left`);
            this.overlay.setOffset([left - x, vertical === "top" ? 20 : -20]);
            this.overlay.setPosition(coordinate);
        },
        closePopup() {
            clearTimeout(this.hoverTimer);
            clearTimeout(this.closeTimer);
            this.hoveredGeos = [];
            if (!this.popup.geos.length) {
                return;
            }
            this.popup = { geos: [], layerId: null };
        },
        geosExtent() {
            const extent = createEmpty();
            Object.values(this.entries).forEach((entry) => {
                if (entry.descriptor.kind === "regions") {
                    entry.descriptor.geos.forEach((geo) => {
                        const feature = entry.source.getFeatureById(
                            geo.country_code
                        );
                        if (feature) {
                            extend(
                                extent,
                                mainlandExtent(feature.getGeometry())
                            );
                        }
                    });

                    return;
                }
                extend(extent, entry.source.getExtent());
            });

            return extent;
        },
        fitOnce() {
            if (!this.fit || this.fitted) {
                return;
            }
            const extent = this.geosExtent();
            if (isEmpty(extent)) {
                return;
            }
            this.fitted = true;
            this.fitView(extent, this.fitOptions);
        },
        fitView(
            extent,
            { padding = [20, 20, 20, 20], maxZoom = 5, closeMaxZoom = 8 }
        ) {
            const view = this.map.getView();
            const size = this.map.getSize();
            const paddedSize = size && [
                size[0] - padding[1] - padding[3],
                size[1] - padding[0] - padding[2],
            ];
            const resolution = paddedSize
                ? view.getResolutionForExtent(extent, paddedSize)
                : undefined;
            const fitZoom = resolution
                ? view.getZoomForResolution(resolution)
                : undefined;

            if (fitZoom !== undefined && fitZoom > maxZoom) {
                view.setCenter(getCenter(extent));
                view.setZoom(
                    Math.max(Math.min(fitZoom, closeMaxZoom) - 1.5, 1)
                );
                view.fit(extent, {
                    maxZoom: closeMaxZoom,
                    padding,
                    duration: 600,
                    easing: easeOut,
                });

                return;
            }

            view.fit(extent, { maxZoom, padding, duration: 1000 });
        },
        getTileLayerSource() {
            const maptilerKey = window.tripper?.maptilerKey;
            if (
                this.tile === "satellite" &&
                this.consentStore.externalAllowed &&
                maptilerKey
            ) {
                return new XYZ({
                    url: `https://api.maptiler.com/maps/hybrid/256/{z}/{x}/{y}@2x.jpg?key=${maptilerKey}`,
                    maxZoom: 10,
                    attributions:
                        '<a href="https://www.maptiler.com/copyright/" target="_blank">' +
                        "&copy; MapTiler</a> " +
                        '<a href="https://www.openstreetmap.org/copyright" target="_blank">' +
                        "&copy; OpenStreetMap contributors</a>",
                });
            }

            return new OSM({
                attribution: [OSM.ATTRIBUTION],
            });
        },
    },
};
</script>
